// Pipes - DOM rendering, tap-to-rotate interaction, and the GameDef export.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import {
  type Conn,
  type PipesLevel,
  cellMatched,
  generatePipesLevel,
  isSolved,
  matchedEdges,
  minRotationDistance,
  rotateCw,
} from "./engine";
import "./pipes.css";

// Endless: each level is generated on demand, deterministically from its number.
const levelCache = new Map<number, PipesLevel>();
function levelForNumber(n: number): PipesLevel {
  let lvl = levelCache.get(n);
  if (!lvl) {
    lvl = generatePipesLevel(n);
    levelCache.set(n, lvl);
  }
  return lvl;
}

function starsFor(taps: number, par: number): number {
  if (taps <= par) return 3;
  if (taps <= Math.ceil(par * 1.5)) return 2;
  return 1;
}

// Edge midpoints per direction bit, in a 100x100 viewBox (kept just inside the
// tile edge so round line caps reach across the border to the neighbor).
const EDGE: Record<number, [number, number]> = {
  1: [50, 4], // N
  2: [96, 50], // E
  4: [50, 96], // S
  8: [4, 50], // W
};

/** Inline SVG for a tile drawn in the given orientation (bitmask). */
function tileSvg(conn: Conn): string {
  let lines = "";
  for (const bit of [1, 2, 4, 8]) {
    if (conn & bit) {
      const [x, y] = EDGE[bit];
      lines += `<line x1="50" y1="50" x2="${x}" y2="${y}"/>`;
    }
  }
  return `<svg class="pp-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">${lines}<circle class="pp-hub" cx="50" cy="50" r="9"/></svg>`;
}

function createLevelView(opts: LevelViewOptions): LevelView {
  const level = levelForNumber(opts.level);
  const { w, h, par } = level;
  const size = w * h;

  let cells: Conn[] = level.start.slice();
  const rot: number[] = new Array<number>(size).fill(0); // CW quarter-turns applied per tile
  let taps = 0;
  let finished = false;
  const timers: Array<ReturnType<typeof setTimeout>> = [];
  const rotEls: HTMLElement[] = [];
  const tileEls: HTMLElement[] = [];

  const root = opts.mount;
  clear(root);
  root.classList.add("pp");

  const boardEl = el("div.pp-board");
  boardEl.style.gridTemplateColumns = `repeat(${w}, 1fr)`;
  const stage = el("div.pp-stage", {}, boardEl);
  const moveLabel = el("span.pp-moves");
  const toolbar = el(
    "div.pp-toolbar",
    {},
    el("button.pp-btn", { onclick: doHint, "aria-label": "Hint" }, "💡 Hint"),
    moveLabel,
    el("button.pp-btn", { onclick: doRestart, "aria-label": "Opnieuw" }, "↺ Opnieuw"),
  );
  root.append(stage, toolbar);

  function later(fn: () => void, ms: number): void {
    timers.push(setTimeout(fn, ms));
  }

  // ---- Build the board once; taps mutate tiles in place so rotation animates.
  function build(): void {
    clear(boardEl);
    rotEls.length = 0;
    tileEls.length = 0;
    for (let i = 0; i < size; i++) {
      const rotEl = el("span.pp-rot", { html: tileSvg(level.start[i]) });
      const tile = el("button.pp-tile", { "aria-label": "Draai buis" }, rotEl);
      tile.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        onTap(i);
      });
      rotEls[i] = rotEl;
      tileEls[i] = tile;
      boardEl.append(tile);
    }
  }

  function paint(): void {
    for (let i = 0; i < size; i++) {
      const live = cellMatched(level, cells, i);
      tileEls[i].classList.toggle("pp-live", live);
      tileEls[i].classList.toggle("pp-dead", !live);
    }
    const { matched, total } = matchedEdges(level, cells);
    moveLabel.textContent = `Zetten: ${taps} · Par ${par}`;
    opts.setStatus?.(`🔗 ${total ? Math.round((100 * matched) / total) : 0}%`);
  }

  // ---- Interaction ----
  function onTap(i: number): void {
    if (finished) return;
    const before = matchedEdges(level, cells).matched;
    rot[i]++;
    cells[i] = rotateCw(cells[i]);
    rotEls[i].style.transform = `rotate(${rot[i] * 90}deg)`;
    taps++;
    haptic(4);
    paint();
    const after = matchedEdges(level, cells).matched;
    if (after > before) sfx.place();
    else sfx.tap();
    if (isSolved(level, cells)) win();
  }

  // ---- Win ----
  function win(): void {
    if (finished) return;
    finished = true;
    boardEl.classList.add("pp-win");
    sfx.win();
    haptic([20, 40, 20]);
    later(() => opts.onWin({ stars: starsFor(taps, par), moves: taps }), 420);
  }

  // ---- Toolbar ----
  function doHint(): void {
    if (finished) return;
    // First tile that is not yet at its solved orientation (modulo symmetry).
    let target = -1;
    for (let i = 0; i < size; i++) {
      if (minRotationDistance(cells[i], level.solved[i]) !== 0) {
        target = i;
        break;
      }
    }
    if (target < 0) {
      sfx.error();
      return;
    }
    sfx.tap();
    const tile = tileEls[target];
    tile.classList.add("pp-hint");
    later(() => tile.classList.remove("pp-hint"), 1000);
  }

  function doRestart(): void {
    finished = false;
    boardEl.classList.remove("pp-win");
    cells = level.start.slice();
    rot.fill(0);
    taps = 0;
    for (let i = 0; i < size; i++) rotEls[i].style.transform = "rotate(0deg)";
    paint();
  }

  // ---- Init ----
  build();
  paint();

  return {
    destroy() {
      for (const t of timers) clearTimeout(t);
    },
    restart: doRestart,
  };
}

export const pipes: GameDef = {
  id: "pipes",
  title: "Pipes",
  tagline: "Draai de buizen tot alles stroomt",
  accent: "#2fa84f",
  icon: "🚰",
  levelCount: Infinity,
  createLevelView,
};
